import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled  from 'styled-components';

import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';
import SkeletonPattern from '@mlb/react-patterns/components/Skeleton/skeleton.tsx';
import COLORS from '@mlb/react-patterns/style/universal.color';
import FONTS from '@mlb/react-patterns/style/fonts.ts';
import { media } from '@mlb/react-patterns/style/helpers/media.ts';

import { mainCq } from 'bdvp-common/src/styles/helpers/main-cq';
import { playbackPage } from 'bdvp-common/src/styles/theme';

import { Tracking } from 'bdvp-tracking';

import {
    useMedia,
    usePagination,
    useMainContainerQuery,
} from 'bdvp-common/src/hooks/';
import ContentCardsGroup from 'bdvp-common/src/components/ContentCardsGroup';
import PopoverContent, { POPOVER_HORIZONTAL_BORDER } from 'bdvp-common/src/components/Card/PopoverContent';

const WRAPPER_HORIZONTAL_PADDING = 12;

const Wrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 0 ${WRAPPER_HORIZONTAL_PADDING}px 30px;

    ${PopoverContent} {
        ${media.xsmax`
            width: calc(100vw - ${WRAPPER_HORIZONTAL_PADDING * 2}px - ${POPOVER_HORIZONTAL_BORDER * 2}px);
        `}
    }

   ${mainCq.md`
        padding: 18px 16px 30px;
    `}

    ${mainCq.lg`
        padding: 0;
        margin-bottom: 30px;
    `}

    .p-ad {
        margin: 0;
       ${mainCq.md`
            position: absolute;
            top: 8px;
            right: 16px;
        `}
        ${mainCq.lg`
            position: initial;
            top: initial;
            right: initial;
        `}
        .ad-responsive-size > div:not(:empty) {
            margin: 0 0 13px !important;
            text-align: left !important;
           ${mainCq.md`
                margin: 0 !important;
                text-align: left !important;
            `}
            ${mainCq.lg`
                margin: 0 0 13px !important;
                text-align: left !important;
            `}
        }
    }
`;

const Title = styled.h2`
    font-family: ${FONTS.primaryFont};
    font-size: 24px;
    font-weight: bold;
    color: ${COLORS.mlbWhite};
    background-color: ${COLORS.mlbBlack};
    margin-bottom: 0;
    padding-bottom: 10px;
    z-index:  0;
    ${mainCq.lg`
        font-size: 20px;
        z-index: 2;
        position: sticky;
        top: ${({ theme }) => theme?.subnav?.height};
        margin-top: ${({ theme }) => `-${theme?.playbackPage?.padding?.top}`};
        padding-top: ${({ theme }) => theme?.playbackPage?.padding?.top};
    `}
`;

const Skeleton = styled(SkeletonPattern)`
    &&& {
        min-width: 166px;
        height: 20px;
        margin-bottom: 10px;

       ${mainCq.md`
            height: 24px;
            margin-bottom: 15px;
        `}

        ${mainCq.lg`
            margin-bottom: 17px;
        `}
    }
`;

const LIMIT = 9;

const Playlist = (props) => {

    const {
        basePath,
        config,
        dictionary,
        hasMore,
        isLoading,
        lang,
        loadMore,
        loadingMore,
        initialMediaItem,
        mediaItem,
        mediaItemList,
        onItemClick,
        sponsorshipAd,
        topicTitle,
        topicId,
        modelVersion,
        modelType,
        puid,
        searchParams,
        topicError,
    } = props;

    const verticalGutter = useMedia(
        [`(min-width: ${BREAKPOINTS.md}) and (max-width: ${parseInt(BREAKPOINTS.lg) - 1}px)`],
        [25],
        12
    );

    const {
        limit,
        sizes,
        imgSizes,
    } = useMainContainerQuery(
        [
            'LG',
            'MD',
            'SM',
            'XS',
        ],
        [
            {
                limit: LIMIT,
                sizes: {
                    xs: 100,
                },
                imgSizes: playbackPage.playlist.width,
            },
            {
                limit: LIMIT,
                sizes: {
                    xs: 33.33,
                },
            },
            {
                limit: LIMIT - 1,
                sizes: {
                    xs: 50,
                },
            },
            {
                limit: LIMIT,
                sizes: {
                    xs: 100,
                },
            },
        ],
        {
            limit: LIMIT,
            sizes: {
                xs: 100,
                sm: 50,
                md: 33.33,
                lg: 100,
                xl: 100,
            },
            imgSizes: undefined,
        }
    );

    const {
        paginatedList,
        hasMoreItems,
        getMoreItems,
    } = usePagination({
        list: mediaItemList,
        limit,
        hasMore,
        fetchMore: loadMore,
        isLoading,
    });

    const showingSkeleton = isLoading || (!topicError && paginatedList.length < 1);

    useEffect(() => {

        if (
            !showingSkeleton
            && hasMoreItems
            && paginatedList.length > 1
            && paginatedList[paginatedList.length - 1].slug === mediaItem.slug
        ) {

            getMoreItems();

        };

    }, [
        hasMoreItems,
        showingSkeleton,
        mediaItem,
        getMoreItems,
        paginatedList,
    ]);

    useEffect(() => {

        if (!initialMediaItem.slug || !modelType) {

            return;

        }

        Tracking.trackRecsForPlaylist({
            currentSlug: initialMediaItem.slug,
            puid,
            modelType,
            modelVersion,
            topicId,
        });

    }, [
        initialMediaItem.slug,
        puid,
        modelType,
        modelVersion,
        topicId,
    ]);

    const seeMoreBtnHandler = () => {

        getMoreItems();
        Tracking.trackPlaybackPageSeeMoreButtonClick();

    };

    return (
        <Wrapper>
            {sponsorshipAd}
            {showingSkeleton
                ? <Skeleton isLoading={showingSkeleton} width={'30%'} />
                : <Title>{topicTitle}</Title>
            }
            <ContentCardsGroup
                activeItem={mediaItem}
                basePath={basePath}
                config={config}
                handleItemClick={onItemClick}
                hasMore={hasMoreItems}
                isLoading={showingSkeleton}
                lang={lang}
                list={paginatedList}
                loadingMore={loadingMore}
                loadMoreButtonHandler={seeMoreBtnHandler}
                dictionary={dictionary}
                limit={limit}
                orientation={'portrait'}
                sizes={sizes}
                verticalGutter={verticalGutter}
                searchParams={searchParams}
                imgSizes={imgSizes}
            />
        </Wrapper>
    );

};

Playlist.propTypes = {
    basePath: PropTypes.string.isRequired,
    config: PropTypes.object,
    dictionary: PropTypes.object,
    hasMore: PropTypes.bool,
    isLoading: PropTypes.bool,
    lang: PropTypes.string,
    loadMore: PropTypes.func.isRequired,
    loadingMore: PropTypes.bool,
    initialMediaItem: PropTypes.object,
    mediaItem: PropTypes.object,
    mediaItemList: PropTypes.array,
    onItemClick: PropTypes.func.isRequired,
    sponsorshipAd: PropTypes.element,
    topicTitle: PropTypes.string.isRequired,
    topicId: PropTypes.string,
    modelVersion: PropTypes.string,
    modelType: PropTypes.string,
    puid: PropTypes.string,
    searchParams: PropTypes.func,
    topicError: PropTypes.object,
};

Playlist.defaultProps = {
    config: {},
    dictionary: {},
    hasMore: false,
    isLoading: false,
    lang: 'en',
    loadingMore: false,
    initialMediaItem: {},
    mediaItem: {},
    mediaItemList: [],
    sponsorshipAd: null,
    modelVersion: '',
    modelType: '',
    puid: null,
    searchParams: undefined,
};

export default Playlist;
